import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowDown, ArrowRight, ArrowUp, CalendarRange, CircleAlert, Crosshair, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { toast } from 'react-toastify';

import { AuthContext } from 'src/context/AuthContext';
import StagePage from 'src/stage/StagePage';
import { WsDropdown, WsSegmented } from 'src/stage/WsControls';
import { Skeleton, formatNumber } from 'src/stage/StageUi';
import { characterFor } from 'src/stage/characters';
import { PersonAvatar } from 'src/features/MiniCRM/Workspace/pages/TeamDirectory';
import { StageDialog } from '../shared/StageKit';
import { apiMessage } from '../shared/crmWork';
import TargetCard from './TargetCard';
import TargetDialog from './TargetDialog';
import { deleteTarget, getRecap, listTargets, targetOptions } from './targetsApi';
import { METRICS, NEEDS_WORK, PERIODS, STATUS, STATUS_RANK, formatValue, issueName, paceLine, periodPhrase, targetTitle, unitWord } from './targetMeta';
import './targets.css';

// AUTO-CRM-32: targets are the point of the CRM: what we aimed for, where we
// are, where we should be by now, and which step is holding us back.
const pad = (n) => String(n).padStart(2, '0');
const dayKey = (date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
function asOfDate(key, now = new Date()) {
  const date = new Date(now);
  if (key === 'yesterday') date.setDate(date.getDate() - 1);
  else if (key === 'last_week') date.setDate(date.getDate() - (((date.getDay() + 6) % 7) + 1));
  else if (key === 'last_month') date.setDate(0);
  else if (key === 'last_quarter') date.setMonth(Math.floor(date.getMonth() / 3) * 3, 0);
  else return null;
  return dayKey(date);
}
const AS_OF = [
  { value: 'now', label: 'Right now', short: 'Now' },
  { value: 'yesterday', label: 'End of yesterday', short: 'Yesterday' },
  { value: 'last_week', label: 'End of last week', short: 'Last week' },
  { value: 'last_month', label: 'End of last month', short: 'Last month' },
  { value: 'last_quarter', label: 'End of last quarter', short: 'Last quarter' },
];
const worstFirst = (a, b) => (STATUS_RANK[a.progress?.status] ?? 2) - (STATUS_RANK[b.progress?.status] ?? 2) || (a.progress?.pace_percent ?? 100) - (b.progress?.pace_percent ?? 100);
const STARTERS = [
  { metric: 'new_leads', period: 'monthly', value: 1000, text: 'Get 1,000 new leads a month' },
  { metric: 'leads_contacted', period: 'daily', value: 30, text: 'Contact 30 leads a day' },
  { metric: 'calls', period: 'daily', value: 50, text: 'Make 50 calls a day' },
  { metric: 'meetings', period: 'weekly', value: 15, text: 'Hold 15 meetings a week' },
  { metric: 'won', period: 'monthly', value: 20, text: 'Win 20 deals a month' },
  { metric: 'stage_reached', period: 'monthly', value: 200, text: 'Bring 200 leads to a stage, like Sales call' },
];

function Diagnosis({ loading, targets, scopeLabel, asOfLabel, onSet, canSet, finished }) {
  const atlas = characterFor('atlas');
  const open = targets.filter((target) => target.progress?.status !== 'done').sort(worstFirst);
  const worst = open[0];
  const issue = worst && NEEDS_WORK.includes(worst.progress.status) ? worst : null;
  const others = open.slice(issue ? 1 : 0).filter((target) => NEEDS_WORK.includes(target.progress.status)).slice(0, 3);
  const doneCount = targets.filter((target) => target.progress?.status === 'done').length;
  const onTrack = targets.filter((target) => ['done', 'ahead', 'on_track'].includes(target.progress?.status)).length;
  let title;
  let text;
  if (loading) { title = null; text = null; }
  else if (!targets.length) { title = 'No targets yet.'; text = 'Set a goal for leads, calls, meetings or wins. Progress fills in from the work your team logs, so everyone can see where you stand.'; }
  else if (issue) {
    const progress = issue.progress;
    title = finished
      ? `${issueName(issue)} fell short ${periodPhrase(issue.period, true)}.`
      : `${issueName(issue)} is ${progress.status === 'at_risk' ? 'falling behind' : 'behind'} ${PERIODS[issue.period].this}.`;
    text = finished
      ? `${formatValue(issue, progress.actual)} of ${formatValue(issue, issue.target_value)} ${unitWord(issue, issue.target_value)} (${Math.round(progress.percent)}%). ${paceLine(issue, true)}`
      : `${formatValue(issue, progress.actual)} of ${formatValue(issue, issue.target_value)} ${unitWord(issue, issue.target_value)} so far (${Math.round(progress.percent)}%). By now it should be ${formatValue(issue, issue.metric === 'revenue' ? progress.expected_by_now : Math.round(progress.expected_by_now))}. ${paceLine(issue)}`;
  } else {
    title = doneCount === targets.length ? 'Every target is hit.' : finished ? 'Every target ended on track.' : 'Everything is on track.';
    text = `${onTrack} of ${targets.length} ${targets.length === 1 ? 'target is' : 'targets are'} on track or better${doneCount ? `, ${doneCount} already hit` : ''}. Keep the pace steady.`;
  }
  return (
    <section className={`ws-spot crm-diag ws-rise${issue ? ` is-${issue.progress.status === 'missed' ? 'at_risk' : issue.progress.status}` : ''}`} aria-label="Where you stand">
      <div className="ws-spot-art crm-home-spot-halo" aria-hidden="true" />
      {atlas?.full && <img className="ws-spot-art crm-diag-art" src={atlas.full} alt="" aria-hidden="true" />}
      <span className="ws-eyebrow"><Crosshair size={12} aria-hidden="true" />{scopeLabel} · {asOfLabel}</span>
      {loading ? <><Skeleton width="70%" height={26} style={{ marginTop: 14, opacity: 0.25 }} /><Skeleton width="85%" style={{ marginTop: 12, opacity: 0.2 }} /></> : (
        <>
          <h2 className="crm-diag-title">{title}</h2>
          <p className="ws-muted crm-diag-text">{text}</p>
          {others.length > 0 && (
            <div className="crm-diag-others">
              <span>{finished ? 'Also short:' : 'Also behind:'}</span>
              {others.map((target) => <span key={target.id} className={`crm-diag-pill is-${STATUS[target.progress.status].tone}`}>{issueName(target)} · {Math.round(target.progress.percent)}%</span>)}
            </div>
          )}
          <div className="crm-diag-actions">
            {issue && ['calls', 'conversations', 'meetings', 'leads_contacted'].includes(issue.metric) && <Link className="ws-btn ws-btn--white ws-btn--sm" to="/follow-ups">Open follow-ups<ArrowRight size={14} /></Link>}
            {issue && !['calls', 'conversations', 'meetings', 'leads_contacted'].includes(issue.metric) && <Link className="ws-btn ws-btn--white ws-btn--sm" to="/crm">Open leads<ArrowRight size={14} /></Link>}
            {!targets.length && canSet && <button type="button" className="ws-btn ws-btn--white ws-btn--sm" onClick={onSet}><Plus size={14} />Set a target</button>}
          </div>
        </>
      )}
    </section>
  );
}

function Delta({ now, before }) {
  if (now === before || before === undefined) return null;
  const up = now > before;
  return <span className={`crm-recap-delta${up ? ' is-up' : ' is-down'}`} title={`Yesterday: ${before}`}>{up ? <ArrowUp size={11} /> : <ArrowDown size={11} />}{Math.abs(now - before)}</span>;
}

function Recap({ recap, scope, onScope, canCompany, finished }) {
  const rows = [['new_leads', 'New leads'], ['leads_contacted', 'Leads contacted'], ['calls', 'Calls'], ['meetings', 'Meetings'], ['moved_forward', 'Moved forward'], ['won', 'Won']];
  const planned = [['follow_ups', 'Follow-ups'], ['calls', 'Calls'], ['meetings', 'Meetings'], ['messages', 'Messages']];
  return (
    <section className="ws-card crm-recap ws-rise" aria-label="Yesterday, today and tomorrow">
      <header className="crm-recap-head">
        <h2 className="ws-h3 ws-grow">Yesterday · Today · Tomorrow</h2>
        {canCompany && <WsSegmented label="Whose numbers" value={scope} onChange={onScope} items={[{ id: 'company', label: 'Company' }, { id: 'me', label: 'Mine' }]} />}
      </header>
      <div className="crm-recap-grid">
        <div className="crm-recap-col">
          <h3>{finished ? 'Day before' : 'Yesterday'}</h3>
          {rows.map(([key, label]) => <div key={key} className="crm-recap-row"><span>{label}</span><b>{recap ? formatNumber(recap.yesterday[key]) : '—'}</b></div>)}
        </div>
        <div className="crm-recap-col is-today">
          <h3>{finished ? 'That day' : 'Today so far'}</h3>
          {rows.map(([key, label]) => <div key={key} className="crm-recap-row"><span>{label}</span><b>{recap ? formatNumber(recap.today[key]) : '—'}{recap && finished && <Delta now={recap.today[key]} before={recap.yesterday[key]} />}</b></div>)}
        </div>
        <div className="crm-recap-col is-next">
          <h3>{finished ? 'Next day, planned' : 'Tomorrow, planned'}</h3>
          {planned.map(([key, label]) => <div key={key} className="crm-recap-row"><span>{label}</span><b>{recap ? formatNumber(recap.tomorrow[key]) : '—'}</b></div>)}
          {recap?.tomorrow.overdue > 0 && <Link to="/follow-ups" className="crm-recap-overdue"><CircleAlert size={13} aria-hidden="true" />{formatNumber(recap.tomorrow.overdue)} overdue now</Link>}
        </div>
      </div>
    </section>
  );
}

// The steps from leads to revenue for one period, and where the drop is.
function Ladder({ targets, finished }) {
  const byPeriod = ['monthly', 'weekly', 'quarterly', 'daily'].map((period) => [period, targets.filter((target) => target.period === period)]).find(([, list]) => new Set(list.map((target) => `${target.metric}:${target.stage?.id || ''}`)).size >= 2);
  if (!byPeriod) return null;
  const [period, list] = byPeriod;
  const steps = [...list].sort((a, b) => (METRICS[a.metric]?.order ?? 9) - (METRICS[b.metric]?.order ?? 9));
  const weakest = [...steps].filter((step) => step.progress?.status !== 'done').sort(worstFirst)[0];
  return (
    <section className="ws-card crm-ladder ws-rise" aria-label="Path to the target">
      <header className="crm-ladder-head">
        <div className="ws-grow"><h2 className="ws-h3">Path to {periodPhrase(period, finished)}’s targets</h2><p className="ws-muted">Each step feeds the next. The red step is where you’re losing the most ground.</p></div>
      </header>
      <ol className="crm-ladder-steps">
        {steps.map((step, index) => {
          const progress = step.progress || {};
          const previous = steps[index - 1];
          const flow = previous && previous.unit !== 'currency' && step.unit !== 'currency' && previous.progress?.actual > 0 ? Math.round((progress.actual / previous.progress.actual) * 100) : null;
          const Icon = METRICS[step.metric]?.icon || Crosshair;
          const tone = STATUS[progress.status]?.tone || 'ok';
          return (
            <li key={step.id} className={`crm-ladder-step is-${tone}${weakest && step.id === weakest.id && ['behind', 'risk'].includes(tone) ? ' is-weakest' : ''}`}>
              {index > 0 && <span className="crm-ladder-flow" aria-hidden={flow === null}>{flow !== null ? `${flow}% of the step before` : ''}</span>}
              <span className="crm-ladder-icon" aria-hidden="true"><Icon size={15} /></span>
              <b>{targetTitle(step)}</b>
              <span className="crm-ladder-num">{formatValue(step, progress.actual)}<small> / {formatValue(step, step.target_value)}</small></span>
              <span className="crm-ladder-bar"><i style={{ width: `${Math.min(100, progress.percent || 0)}%` }} /></span>
              <span className={`crm-target-status is-${tone}`}>{STATUS[progress.status]?.label}</span>
            </li>
          );
        })}
      </ol>
    </section>
  );
}

function FollowThrough({ data, days, onDays }) {
  const later = data ? data.contacted - data.contacted_within_hour : 0;
  const waiting = data ? Math.max(0, data.total - data.contacted) : 0;
  const width = (value) => `${data?.total ? (value / data.total) * 100 : 0}%`;
  const minutes = data?.median_first_response_minutes;
  const response = minutes === null || minutes === undefined ? '—' : minutes < 60 ? `${minutes} min` : `${Math.round((minutes / 60) * 10) / 10} h`;
  return (
    <section className="ws-card crm-through ws-rise" aria-label="What happened to new leads">
      <header className="crm-recap-head">
        <div className="ws-grow"><h2 className="ws-h3">What happened to new leads</h2><p className="ws-muted">Of the leads that came in, who was contacted, how fast, and who is still waiting.</p></div>
        <WsSegmented label="New leads from" value={days} onChange={onDays} items={[{ id: 1, label: 'Today' }, { id: 7, label: '7 days' }, { id: 30, label: '30 days' }]} />
      </header>
      {!data ? <Skeleton height={120} radius={16} /> : data.total === 0 ? <p className="crm-through-empty">No new leads in this period yet.</p> : (
        <>
          <div className="crm-through-top">
            <div className="crm-through-big"><b>{formatNumber(data.total)}</b><span>new {data.total === 1 ? 'lead' : 'leads'}</span></div>
            <div className="crm-through-bar" role="img" aria-label={`${data.contacted_within_hour} contacted within an hour, ${later} contacted later, ${waiting} not contacted yet`}>
              <i className="is-fast" style={{ width: width(data.contacted_within_hour) }} />
              <i className="is-later" style={{ width: width(later) }} />
              <i className="is-waiting" style={{ width: width(waiting) }} />
            </div>
          </div>
          <ul className="crm-through-legend">
            <li><i className="is-fast" />Within an hour <b>{formatNumber(data.contacted_within_hour)}</b></li>
            <li><i className="is-later" />Later <b>{formatNumber(later)}</b></li>
            <li><i className="is-waiting" />Not contacted yet <b>{formatNumber(waiting)}</b></li>
          </ul>
          <div className="crm-through-stats">
            <div><span>Typical first reply</span><b>{response}</b></div>
            <div><span>Moved forward</span><b>{formatNumber(data.moved_forward)}</b></div>
            <div><span>Won</span><b>{formatNumber(data.won)}</b></div>
            <div className={data.no_next_step ? 'is-warn' : ''}><span>No next step</span><b>{formatNumber(data.no_next_step)}</b></div>
          </div>
          {data.by_source?.length > 0 && (
            <table className="crm-through-sources">
              <thead><tr><th scope="col">Source</th><th scope="col">Leads</th><th scope="col">Contacted</th></tr></thead>
              <tbody>{data.by_source.map((row) => {
                const share = row.total ? Math.round((row.contacted / row.total) * 100) : 0;
                return <tr key={row.source}><td>{row.source}</td><td>{formatNumber(row.total)}</td><td><span className="crm-through-share"><span className="ws-meter"><span style={{ width: `${share}%` }} /></span>{share}%</span></td></tr>;
              })}</tbody>
            </table>
          )}
        </>
      )}
    </section>
  );
}

const PERIOD_ORDER = { daily: 1, weekly: 2, monthly: 3, quarterly: 4 };

// Every target in one grid, shortest period first; filter to one period.
function AllTargets({ targets, period, onPeriod, canManage, onEdit, onDelete, showOwner, finished }) {
  const shown = targets.filter((target) => period === 'all' || target.period === period)
    .sort((a, b) => PERIOD_ORDER[a.period] - PERIOD_ORDER[b.period] || worstFirst(a, b));
  const onTrack = shown.filter((target) => ['done', 'ahead', 'on_track'].includes(target.progress?.status)).length;
  const items = [{ id: 'all', label: 'All', count: targets.length }, ...Object.entries(PERIODS)
    .map(([id, item]) => ({ id, label: item.section, count: targets.filter((target) => target.period === id).length })).filter((item) => item.count)];
  return (
    <section className="crm-tsection" aria-label="Targets">
      <header>
        <WsSegmented label="Period" value={period} onChange={onPeriod} items={items} />
        <span className="crm-tsection-count">{onTrack} of {shown.length} on track or better</span>
      </header>
      <ul className="crm-target-grid">{shown.map((target) => <TargetCard key={target.id} target={target} canManage={canManage(target)} onEdit={onEdit} onDelete={onDelete} showOwner={showOwner} finished={finished} />)}</ul>
    </section>
  );
}

export default function TargetsPage() {
  const { currentUser } = useContext(AuthContext);
  const [view, setView] = useState('company');
  const [asOf, setAsOf] = useState('now');
  const [targets, setTargets] = useState(null);
  const [meta, setMeta] = useState({});
  const [options, setOptions] = useState(null);
  const [error, setError] = useState('');
  const [recapScope, setRecapScope] = useState('company');
  const [recapDays, setRecapDays] = useState(1);
  const [recap, setRecap] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [removing, setRemoving] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tick, setTick] = useState(0);
  const [periodFilter, setPeriodFilter] = useState('all');
  const date = asOfDate(asOf);

  const load = useCallback(async () => {
    setError('');
    try {
      const result = await listTargets(date);
      setTargets(result.data || []);
      setMeta(result.meta || {});
    } catch (loadError) {
      setTargets([]);
      setError(apiMessage(loadError, 'We couldn’t load your targets.'));
    }
  }, [date]);
  useEffect(() => { setTargets(null); load(); }, [load]);
  useEffect(() => { targetOptions().then(setOptions).catch(() => setOptions(null)); }, []);
  useEffect(() => {
    let active = true;
    setRecap(null);
    getRecap({ scope: recapScope, days: recapDays, date }).then((data) => { if (active) setRecap(data); }).catch(() => { if (active) setRecap(undefined); });
    return () => { active = false; };
  }, [recapScope, recapDays, date, tick]);

  const me = options?.me || currentUser?.id;
  const canCompany = Boolean(meta.can_manage_company ?? options?.can_manage_company);
  const canPeople = Boolean(meta.can_manage_people ?? options?.can_manage_people);
  useEffect(() => { if (options && !options.can_manage_company) setRecapScope('me'); }, [options]);
  const canManage = (target) => (target.scope === 'company' ? canCompany : String(target.user?.id) === String(me) || canPeople);
  const list = useMemo(() => (targets || []).filter((target) => (view === 'company' ? target.scope === 'company' : view === 'mine' ? String(target.user?.id) === String(me) : target.scope === 'person')), [targets, view, me]);
  const byPerson = useMemo(() => {
    const groups = new Map();
    list.forEach((target) => { const key = target.user?.id; groups.set(key, [...(groups.get(key) || []), target]); });
    return [...groups.entries()].map(([id, items]) => ({ id, user: items[0].user, items: items.sort(worstFirst) })).sort((a, b) => worstFirst(a.items[0], b.items[0]));
  }, [list]);
  const asOfLabel = AS_OF.find((item) => item.value === asOf)?.label || 'Right now';
  const loading = targets === null;
  const openDialog = (extra = {}) => setDialog({ defaults: { owner: view === 'company' && canCompany ? 'company' : view === 'people' && canPeople ? 'person' : 'me', ...extra } });

  const remove = async () => {
    setBusy(true);
    try {
      await deleteTarget(removing.id);
      toast.success('Target removed');
      setRemoving(null);
      load();
      setTick((value) => value + 1);
    } catch (removeError) {
      toast.error(apiMessage(removeError, 'We couldn’t remove this target.'));
    } finally {
      setBusy(false);
    }
  };

  const views = [{ id: 'company', label: 'Company' }, ...(canPeople ? [{ id: 'people', label: 'People' }] : []), { id: 'mine', label: 'Mine' }];
  const empty = !loading && list.length === 0;

  return (
    <StagePage
      className="crm-targets-page"
      eyebrow={<>Work<span className="crm-eyebrow-dot" />Targets</>}
      title="Targets"
      subtitle="What you aimed for, where you are, where you should be by now, and which step is holding you back."
      actions={(
        <>
          <button type="button" className="crm-icon-btn" onClick={() => { load(); setTick((value) => value + 1); }} aria-label="Refresh" title="Refresh"><RefreshCw size={16} className={loading ? 'ws-spin' : ''} /></button>
          <WsDropdown label="Viewing" icon={CalendarRange} value={asOf} onChange={setAsOf} options={AS_OF} align="right" />
          <button type="button" className="ws-btn ws-btn--primary" onClick={() => openDialog()} disabled={!options}><Plus size={16} />Set a target</button>
        </>
      )}
    >
      {error && <div className="ws-notice" role="alert"><CircleAlert size={16} style={{ flex: 'none', marginTop: 1 }} /><span className="ws-grow">{error}</span><button type="button" className="ws-btn ws-btn--quiet ws-btn--sm" onClick={load}>Try again</button></div>}

      <div className="crm-targets-bar">
        <WsSegmented label="Whose targets" value={view} onChange={setView} items={views.map((item) => ({ ...item, count: targets ? (targets || []).filter((target) => (item.id === 'company' ? target.scope === 'company' : item.id === 'mine' ? String(target.user?.id) === String(me) : target.scope === 'person')).length : undefined }))} />
        {asOf !== 'now' && <span className="crm-targets-asof">Showing how targets ended: {asOfLabel.toLowerCase()} ({date}).</span>}
      </div>

      <div className="crm-targets-top">
        <Diagnosis finished={asOf !== 'now'} loading={loading} targets={list} scopeLabel={view === 'company' ? 'Company targets' : view === 'mine' ? 'Your targets' : 'Team targets'} asOfLabel={asOfLabel} onSet={() => openDialog()} canSet={Boolean(options)} />
        <Recap recap={recap} scope={recapScope} onScope={setRecapScope} canCompany={canCompany} finished={asOf !== 'now'} />
      </div>

      {view === 'company' && !loading && <Ladder targets={list} finished={asOf !== 'now'} />}

      {loading ? (
        <ul className="crm-target-grid">{[0, 1, 2].map((key) => <li key={key}><Skeleton height={230} radius={18} /></li>)}</ul>
      ) : empty ? (
        <section className="ws-card crm-targets-empty" aria-label="Suggested targets">
          <div className="crm-targets-empty-head">
            <span className="crm-targets-empty-icon" aria-hidden="true"><Crosshair size={22} /></span>
            <div><h2 className="ws-h3">{view === 'company' ? (canCompany ? 'Set the company’s targets' : 'No company targets yet') : view === 'mine' ? 'Set yourself a target' : 'No personal targets yet'}</h2>
              <p className="ws-muted">{view === 'company' && !canCompany ? 'Your admin sets company targets. You can still set your own under Mine.' : 'Start from a common goal and adjust the number. Every call, meeting and stage move you log counts toward it.'}</p></div>
          </div>
          {(view !== 'company' || canCompany) && (
            <div className="crm-starters">
              {STARTERS.map((starter) => {
                const Icon = METRICS[starter.metric].icon;
                return <button key={starter.text} type="button" className="crm-starter" onClick={() => openDialog({ metric: starter.metric, period: starter.period, value: starter.value })}><span aria-hidden="true"><Icon size={16} /></span>{starter.text}<ArrowRight size={14} aria-hidden="true" /></button>;
              })}
            </div>
          )}
        </section>
      ) : view === 'people' ? (
        byPerson.map((group) => {
          const onTrack = group.items.filter((target) => ['done', 'ahead', 'on_track'].includes(target.progress?.status)).length;
          return (
            <section key={group.id} className="crm-tsection" aria-label={`${group.user?.name}'s targets`}>
              <header><PersonAvatar name={group.user?.name || '?'} size={30} /><h2 className="ws-h3">{group.user?.name}</h2><span className="crm-tsection-count">{onTrack} of {group.items.length} on track</span></header>
              <ul className="crm-target-grid">{group.items.map((target) => <TargetCard key={target.id} target={target} canManage={canManage(target)} onEdit={(item) => setDialog({ target: item })} onDelete={setRemoving} showOwner={false} finished={asOf !== 'now'} />)}</ul>
            </section>
          );
        })
      ) : (
        <AllTargets targets={list} period={periodFilter} onPeriod={setPeriodFilter} canManage={canManage} onEdit={(item) => setDialog({ target: item })} onDelete={setRemoving} showOwner={view !== 'mine'} finished={asOf !== 'now'} />
      )}

      <FollowThrough data={recap?.follow_through} days={recapDays} onDays={setRecapDays} />

      {dialog && options && <TargetDialog options={options} target={dialog.target} defaults={dialog.defaults} onClose={() => setDialog(null)} onSaved={() => { load(); setTick((value) => value + 1); }} />}
      {removing && (
        <StageDialog title="Remove this target?" icon={Trash2} width={440} busy={busy} onClose={() => setRemoving(null)}
          footer={<><span className="ws-grow" /><button type="button" className="ws-btn ws-btn--quiet" onClick={() => setRemoving(null)} disabled={busy}>Keep it</button><button type="button" className="ws-btn ws-btn--danger" onClick={remove} disabled={busy}><Trash2 size={14} />{busy ? 'Removing…' : 'Remove target'}</button></>}>
          <p className="ws-muted">{targetTitle(removing)} ({PERIODS[removing.period]?.label.toLowerCase()}, {removing.user?.name || 'company'}) will no longer be tracked. The work already logged stays in the CRM.</p>
        </StageDialog>
      )}
    </StagePage>
  );
}
